import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useGetEmployeeById } from '../hooks/useEmployeeApi';

import defaultImg from "../assets/default.jpeg"
import Loader from '../components/ui/Loader';
// Helper component for displaying individual data fields to reduce repetition
const DetailField = ({ label, value }) => {
  if (!value) return null; // Don't render if the value is empty
  return (
    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{value}</dd>
    </div>
  );
};

export default function EmployeeDetailPage() {
  const { employeeId } = useParams();
  const { data: employee, isLoading, isError, error } = useGetEmployeeById(employeeId);

  if (isLoading) {
    return <Loader />
  }

  if (isError) {
    return <div className="p-8 text-center text-red-500">Error: {error.message}</div>;
  }

  if (!employee) {
    return <div className="p-8 text-center text-gray-600">Employee not found.</div>;
  }

  const formatEpochDate = (epoch) => {
    if (!epoch) return "—";
    return new Date(epoch * 1000).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const toTitleCase = (str) => {
    if (!str) return "N/A";
    return str
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  return (
    <div className=" w-full flex justify-center  mx-auto p-6 bg-gray-100 min-h-screen">
      <div className="max-w-5xl   bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
        <img
  src={
    employee?.data?.employeePhoto?.trim()
      ? employee.data.employeePhoto
      : defaultImg
  }
  alt="Employee"
  className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
/>


          <div>
            <h1 className="text-2xl font-bold text-gray-900">{toTitleCase(employee.data.name)}</h1>
            <p className="text-sm font-medium text-gray-500">{toTitleCase(employee.data.designation)}</p>
            <p className="text-sm text-gray-500">{employee.data.employeeId}</p>
          </div>
          <div className="sm:ml-auto">
            <Link to={`/employees/edit/${employee.data.employeeId}`} className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700">
              Edit Profile
            </Link>
          </div>
        </div>

        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Personal Information</h3>
            </div>
            <div className="bg-white px-4 py-5 grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <DetailField label="Father's Name" value={toTitleCase(employee.data.fatherName)} />
              <DetailField label="Date of Birth" value={formatEpochDate(employee.data.dob)} />
              <DetailField label="PAN Number" value={employee.data.panNo} />
              <DetailField label="Blood Group" value={employee.data.bloodGroup} />
            </div>

            <div className="bg-gray-50 px-4 py-5">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Contact Information</h3>
            </div>
            <div className="bg-white px-4 py-5 grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <DetailField label="Email Address" value={employee.data.email} />
              <DetailField label="Mobile Number" value={employee.data.mobileNo} />
              <DetailField label="Correspondence Address" value={employee.data.correspondenceAddress} />
            </div>

            <div className="bg-gray-50 px-4 py-5">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Employment Details</h3>
            </div>
            <div className="bg-white px-4 py-5 grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <DetailField label="Joining Date" value={formatEpochDate(employee.data.joiningDate)} />
              <DetailField label="Job Type" value={toTitleCase(employee.data.jobType)} />
              <DetailField label="Previous Job End Date" value={formatEpochDate(employee.data.previousJobDate)} />
              <DetailField label="Department" value={employee?.data?.department} />

            </div>

            <div className="bg-gray-50 px-4 py-5">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Bank Details</h3>
            </div>
            <div className="bg-white px-4 py-5 grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <DetailField label="Bank Name" value={toTitleCase(employee?.data.bankDetails?.bankName)} />
              <DetailField label="Branch" value={employee?.data.bankDetails?.branch} />
              <DetailField label="Account Number" value={employee?.data.bankDetails?.accountNo} />
              <DetailField label="Account Type" value={toTitleCase(employee?.data.bankDetails?.accountType)} />
              <DetailField label="IFSC Code" value={employee?.data.bankDetails?.ifscCode} />
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
}